/* eslint-disable no-param-reassign */
// import { makeStyles, useTheme, Typography } from '@material-ui/core';
import { makeStyles, Typography } from '@material-ui/core';
import * as d3 from 'd3';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import usMapData, { stateMaps } from '../modules/maps';
import { stateFIPS } from '../modules/states';
import FiltersContext from '../contexts/FiltersContext';

export default function StateCensusTractMap({
  data,
  width, height, stateCode, primaryColor,
  toolTipFormatterFunction, hideTooltip = false, colorRowsToUse, noDataColor = '#ccc',
}) {
  const mapRef = useRef(null);
  const tooltipRef = useRef(null);
  const [error, setError] = useState(null);
  const { childRace } = React.useContext(FiltersContext);

  const classes = makeStyles(() => ({
    toolTip: {
      borderColor: primaryColor,
      border: '1px solid',
      position: 'absolute',
      textAlign: 'left',
      maxWidth: '250px',
      padding: '5px',
      font: '1em "ff-scala-sans-pro", Arial, sans-serif',
      background: '#fff',
      pointerEvents: 'none',
      zIndex: 20,
    },
  }))();

  const colors = [
    '#ffffff', '#F8BAB6', '#F2756D',
    '#7FA4C7', '#8D7991', '#9B4E5B',
    '#004990', '#22386D', '#44274A',
  ];

  useEffect(() => {
    async function renderMap(geoJSON) {
      const tracts = geoJSON.features;
      tracts.forEach((tract) => {
        data[tract.id] = { ...data[tract.id], ...tract };
      });

      data = _.values(data);

      const countyData = stateMaps[stateCode.toUpperCase()]; // await import(`@amcharts/amcharts4-geodata/region/usa/${stateCode.toLowerCase()}High`);
      const counties = countyData.features;

      const svg = d3.select(mapRef.current)
        .attr('width', width)
        .attr('height', height)
        .html(null); // clear svg for re-render on resize

      d3.select(tooltipRef.current).style('opacity', 0);

      // get the state object
      const state = usMapData.features.filter((d) => (d.properties.id === `US-${stateCode.toUpperCase()}`))[0];

      // using fitSize to fit the projection within the viewport
      const projection = d3.geoMercator().fitSize([width, height], state);

      // Define path generator
      const path = d3.geoPath().projection(projection);

      // const dr = data.map((d) => (d.value));
      // const colorScale = d3.scaleLinear().domain([_.min(dr), _.max(dr)]).range([colorLow, colorHigh]);

      // console.log(dataRange)
      // console.log(`min: ${_.min(dataRange)} max: ${_.max(dataRange)}`);
      // console.log(colorScale(_.mean(dataRange)));

      function populateTooltip(d) {
        const dataPoint = _.find(data, { county_fips: d.id });
        // use provided tooltip formatter function. The parent component will know all about the data.
        if (toolTipFormatterFunction) {
          return toolTipFormatterFunction(d, dataPoint);
        }
        return 'Fix tooltips!';
      }

      const n = 3; // 3x3 grid of colors
      const x = d3.scaleQuantile(_.uniq(Array.from(data.filter((d) => (_.has(d, 'percentChange'))), (d) => d.percentChange).sort()), d3.range(n));
      // console.log(_.uniq(Array.from(data, (d) => d.percentChange).sort()))
      const y = d3.scaleQuantile(_.uniq(Array.from(data.filter((d) => (_.has(d, 'percentRace'))), (d) => d.percentRace).sort()), d3.range(n));
      // console.log(Array.from(data.filter((d) => (_.has(d, 'percentRace'))), (d) => d.percentRace).sort())
      const color = function color(value) {
        if (!value) return noDataColor;
        const [a, b] = value;
        // console.log(`value: ${value}, x: ${x(a)}, y: ${y(b)}, index: ${x(a) + (y(b) * n)}`);

        return colors[x(a) + (y(b) * colorRowsToUse)]; // colorRowsToUse is passed from Map component. Forces to use only the top row
        // return colors[y(b) + x(a) * n]; // original
      };

      // first create outlines and colors
      svg.append('g')
        .selectAll('path')
        .data(data)
        .enter()
        .append('path')
        .attr('d', path)
        .style('fill', (d) => {
          if (!_.has(d, 'from') || !_.has(d, 'to') || (childRace !== 'All' && !_.has(d, 'percentRace'))) {
            return noDataColor;
          }
          // sometimes we don't have data to calculate % change or % race. Set to 0 if missing to get white.
          return color([d.percentChange || 0, d.percentRace || 0]);
        })
        .style('stroke', '#fff')
        .style('stroke-width', '0.2')
        .attr('class', 'tract');

      // add county borders, transparent backgrounds
      svg.append('g')
        .selectAll('path')
        .data(counties)
        .enter()
        .append('path')
        .attr('d', path)
        .style('fill', 'transparent')
        .style('stroke', '#000000')
        .style('stroke-width', '0.5')
        .attr('class', 'county');

      // add another group of transparent tract shapes and attach events to them
      svg.append('g')
        .selectAll('path')
        .data(data)
        .enter()
        .append('path')
        .attr('d', path)
        .on('mouseover', (d) => {
          if (!hideTooltip) {
            const tooltip = d3.select(tooltipRef.current);
            tooltip.transition().duration(300).style('opacity', 1);
            tooltip.html(populateTooltip(d));
          }
        })
        .on('mousemove', () => {
          const tooltip = d3.select(tooltipRef.current);
          return tooltip.style('top', `${d3.event.pageY - 10}px`).style('left', `${d3.event.pageX + 10}px`);
        })
        .on('mouseout', () => {
          const tooltip = d3.select(tooltipRef.current);
          tooltip.transition().duration(300).style('opacity', 0);
        })
        .style('fill', 'transparent')
        .attr('class', 'tract-event');
    }

    setError(null);
    d3.json(`${process.env.REACT_APP_TRACT_MAPS_PATH}/${stateFIPS[stateCode.toUpperCase()]}.json`)
      .then((geoData) => {
        renderMap(geoData);
      })
      .catch((e) => {
        setError(e);
      });
  }, [stateCode, data, width]);

  return (
    <>
      {error && <Typography variant="body1" color="error">{error.message}</Typography>}
      <svg ref={mapRef} />
      <div ref={tooltipRef} className={classes.toolTip} />
    </>
  );
}

StateCensusTractMap.propTypes = {
  height: PropTypes.number,
  width: PropTypes.number,
  stateCode: PropTypes.string,
};

StateCensusTractMap.defaultProps = {
  height: 100,
  width: 200,
  stateCode: 'NY',
};
